/**
 * The forge on the storefront: the builder's arithmetic (pull rates in percent to weights, the
 * live price, the submit label), the message for each way a write can fail, and the mutations.
 * The price is `forgeBoxPriceCents`, the server's own formula from `@loot-os/api-contract`, so
 * the price a shopper sees is the price the box is published at.
 */
import {
  type BoxDetail,
  type Currency,
  FORGE_MAX_ITEMS,
  FORGE_MIN_ITEMS,
  FORGE_NAME_MAX,
  type ForgeBoxStatusResponse,
  type ForgeBoxStatusValue,
  type ForgeBoxWriteResponse,
  type ForgeClaimResponse,
  forgeBoxPriceCents,
  forgeMarginPercent,
  forgeNameIssue,
  formatAmountWithUnit,
  type StorefrontTerminology,
} from "@loot-os/api-contract";
import { isLootApiErrorLike } from "@loot-os/react";
import { useMutation } from "@tanstack/react-query";

import { invalidateAfterForgeChange, invalidateAfterForgeClaim, lootFetch, qk } from "./queries.js";
import { queryClient } from "./queryClient.js";

/** The weights of a drop table add up to this: parts per million. */
export const FORGE_TOTAL_WEIGHT = 1_000_000;

/** One weight point is this many percent: 100% / 1,000,000. */
const WEIGHT_PER_PERCENT = FORGE_TOTAL_WEIGHT / 100;

/**
 * What the pull-rate field accepts as the shopper types: 0–99 or 100, then up to three decimals
 * (boxed's builder). "" is accepted too, so the field can be cleared.
 */
const PULL_RATE_INPUT = /^(?:100|[0-9]{1,2})(?:\.\d{0,3})?$/;

export function isPullRateInput(text: string): boolean {
  return text === "" || PULL_RATE_INPUT.test(text);
}

/** "12.345" → 123,450. Three decimals of a percent are whole weight points, so this is exact. */
export function percentToWeight(percent: string): number {
  if (percent === "") return 0;
  const value = Number(percent);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.round(value * WEIGHT_PER_PERCENT);
}

/** 123,450 → "12.345", the form the pull-rate field shows a stored weight in. */
export function weightToPercent(weight: number): string {
  return (weight / WEIGHT_PER_PERCENT).toFixed(3);
}

/** The creator's cut in percent (two decimals at most) → basis points, what the price reads. */
export function cutPercentToBps(percent: number): number {
  return Math.round(percent * 100);
}

/** One row of the builder: a catalog item the shopper picked, and its pull rate as typed. */
export interface ForgeRow {
  id: string;
  name: string;
  imageUrl: string;
  valueCents: number;
  /** The pull-rate field's text, in percent. */
  percent: string;
}

/** Σ weight of the rows, in integer weight points: 100% exactly is `FORGE_TOTAL_WEIGHT`. */
export function totalWeight(rows: readonly ForgeRow[]): number {
  return rows.reduce((sum, row) => sum + percentToWeight(row.percent), 0);
}

/**
 * What one row adds to the price, the "+price" beside its pull rate: its value times its chance,
 * over the win rate (boxed's `calculateIndividualAssetContributionPrice`), rounded up.
 */
export function contributionCents(row: ForgeRow, cutBps: number): number {
  const chance = percentToWeight(row.percent) / FORGE_TOTAL_WEIGHT;
  const winRate = 1 - forgeMarginPercent(cutBps) / 100;
  return Math.ceil((row.valueCents * chance) / winRate);
}

/**
 * The price the builder shows. At exactly 100% it is the server's formula; before that it is
 * the sum of the rows' contributions, so the number grows as the rates are typed and the chips
 * add up to it. (`forgeBoxPriceCents` normalizes by the weights' own sum, so at 60% it would
 * already show a full table's price.)
 */
export function livePriceCents(rows: readonly ForgeRow[], cutBps: number): number {
  if (totalWeight(rows) !== FORGE_TOTAL_WEIGHT) {
    return rows.reduce((sum, row) => sum + contributionCents(row, cutBps), 0);
  }
  return forgeBoxPriceCents(
    rows.map((row) => ({
      id: row.id,
      weight: percentToWeight(row.percent),
      value: row.valueCents,
    })),
    cutBps,
  );
}

/** What the creator earns per open at this price and cut, floored to cents. */
export function earningsPerOpenCents(priceCents: number, cutPercent: number): number {
  return Math.floor((priceCents * cutPercent) / 100);
}

/** The drop table as the API takes it. Only called once the weights total 1,000,000. */
export function toDropTableInput(rows: readonly ForgeRow[]) {
  return rows.map((row) => ({ catalogItemId: row.id, weight: percentToWeight(row.percent) }));
}

/** The builder's rows from a published version, for the edit page. */
export function rowsFromDetail(detail: BoxDetail): ForgeRow[] {
  return detail.dropTable.map((entry) => ({
    id: entry.itemId,
    name: entry.name,
    imageUrl: entry.imageUrl,
    valueCents: entry.value,
    percent: weightToPercent(entry.weight),
  }));
}

/** Orders the rows by value, cheapest first or dearest first. */
export function sortRowsByValue(rows: readonly ForgeRow[], direction: "asc" | "desc"): ForgeRow[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => sign * (a.valueCents - b.valueCents));
}

/**
 * The first requirement the builder has not met, in the order the page reads top to bottom, or
 * null when it can be submitted. Editing locks the name and image, so it starts at the items.
 */
export type ForgeSubmitBlocker =
  | "name_empty"
  | "name_invalid"
  | "image"
  | "items"
  | "rates"
  | "total"
  | null;

export interface ForgeBuilderState {
  mode: "create" | "edit";
  name: string;
  imageUrl: string;
  /** The store's preset images; none offered means no image is needed. */
  imagesOffered: number;
  rows: readonly ForgeRow[];
}

export function forgeSubmitBlocker(state: ForgeBuilderState): ForgeSubmitBlocker {
  if (state.mode === "create") {
    const issue = forgeNameIssue(state.name);
    if (issue === "empty") return "name_empty";
    if (issue) return "name_invalid";
    if (state.imagesOffered > 0 && !state.imageUrl) return "image";
  }
  if (state.rows.length < FORGE_MIN_ITEMS) return "items";
  if (state.rows.some((row) => percentToWeight(row.percent) <= 0)) return "rates";
  if (totalWeight(state.rows) !== FORGE_TOTAL_WEIGHT) return "total";
  return null;
}

/** The submit button's label: the first unmet requirement, else the action. */
export function forgeSubmitLabel(state: ForgeBuilderState, terms: StorefrontTerminology): string {
  switch (forgeSubmitBlocker(state)) {
    case "name_empty":
      return "Enter a name";
    case "name_invalid":
      return "Fix the name";
    case "image":
      return "Select an image";
    case "items":
      return `Select at least ${FORGE_MIN_ITEMS} ${terms.item.plural}`;
    case "rates":
      return "Set pull rates";
    case "total":
      return "Pull rates must total 100%";
    case null:
      return state.mode === "edit" ? "Save changes" : `Create ${terms.box.singular}`;
  }
}

/** What the name field says under a name the shopper has typed, or null. */
export function forgeNameHint(name: string): string | null {
  switch (forgeNameIssue(name)) {
    case "too_long":
      return `At most ${FORGE_NAME_MAX} characters.`;
    case "special_characters":
      return "Letters, numbers and spaces only: no punctuation or symbols.";
    default:
      return null;
  }
}

function detailString(details: Record<string, unknown> | undefined, key: string): string | null {
  const value = details?.[key];
  return typeof value === "string" ? value : null;
}

function detailNumber(details: Record<string, unknown> | undefined, key: string): number | null {
  const value = details?.[key];
  return typeof value === "number" ? value : null;
}

export interface ForgeErrorContext {
  terms: StorefrontTerminology;
  currency: Currency;
  /** The name of a picked item by id, so a refusal can say which one. */
  itemName?: (id: string) => string | undefined;
}

/** Why a drop table was refused, per `details.reason`. */
function dropTableText(details: Record<string, unknown> | undefined, ctx: ForgeErrorContext) {
  const { item, box } = ctx.terms;
  const itemId = detailString(details, "itemId");
  const named = (itemId && ctx.itemName?.(itemId)) || `One ${item.singular}`;
  switch (detailString(details, "reason")) {
    case "too_few_items":
      return `Pick at least ${FORGE_MIN_ITEMS} ${item.plural}.`;
    case "too_many_items":
      return `Pick at most ${FORGE_MAX_ITEMS} ${item.plural}.`;
    case "duplicate_item":
      return `${named} is in the ${box.singular} twice.`;
    case "zero_weight":
    case "invalid_weight":
      return `${named} needs a pull rate above 0%.`;
    case "weights_sum":
      return "Pull rates must total exactly 100%.";
    case "item_not_found":
      return `${named} is no longer available. Remove it and try again.`;
    case "item_not_fulfillable":
      return `${named} can no longer be shipped. Remove it and try again.`;
    case "item_below_minimum": {
      const min = detailNumber(details, "minimumCents");
      return min === null
        ? `${named} is worth too little for the forge. Remove it.`
        : `${named} is worth less than ${formatAmountWithUnit(min, ctx.currency)}. Remove it.`;
    }
    case "item_above_maximum": {
      const max = detailNumber(details, "maximumCents");
      return max === null
        ? `${named} is worth too much for the forge. Remove it.`
        : `${named} is worth more than ${formatAmountWithUnit(max, ctx.currency)}. Remove it.`;
    }
    default:
      return `This ${box.singular} cannot be saved as it is. Check the ${item.plural} and pull rates.`;
  }
}

/** What to tell the shopper when a forge write failed. */
export function forgeErrorMessage(err: unknown, ctx: ForgeErrorContext): string {
  const { box } = ctx.terms;
  if (!isLootApiErrorLike(err)) {
    return err instanceof Error && err.message ? err.message : "Something went wrong. Try again.";
  }
  // The published client's ErrorCode predates the forge, so the code is compared as a string.
  const code: string = err.code;
  const details = err.details;
  const reason = detailString(details, "reason");
  switch (code) {
    case "forge_drop_table_invalid":
      return dropTableText(details, ctx);
    case "forge_cut_invalid": {
      if (reason === "commission_disabled") {
        return "This store pays no commission, so your cut must be 0%.";
      }
      const max = detailNumber(details, "maxCreatorCutPercent");
      if (reason === "above_maximum" && max !== null) return `Your cut can be at most ${max}%.`;
      return "Pick a cut between 0% and the store's maximum.";
    }
    case "forge_name_rejected":
      switch (reason) {
        case "empty":
          return "Enter a name.";
        case "too_long":
          return `Names are at most ${FORGE_NAME_MAX} characters.`;
        case "special_characters":
          return "Names can use letters, numbers and spaces only.";
        case "profanity":
          return "That name is not allowed. Try another.";
        default:
          return "That name is not allowed.";
      }
    case "forge_image_invalid":
      return reason === "not_offered"
        ? "That image is no longer offered. Pick another."
        : `Pick an image for your ${box.singular}.`;
    case "forge_daily_limit":
      return `You have forged as many ${box.plural} as you can today. Try again tomorrow.`;
    case "forge_disabled":
      return `The ${ctx.terms.forge.singular} is turned off on this store.`;
    case "forge_nothing_to_claim":
      return "There are no earnings to claim yet.";
    case "username_required":
      return "Choose a username first: other players see it as the creator.";
    case "box_version_changed":
      return `This ${box.singular} changed since you loaded it. The latest pull rates are loaded; review them and save again.`;
    case "platform_switch_disabled":
      return "The forge is paused right now. Try again later.";
    case "rate_limited":
      return "Too many changes at once. Wait a minute and try again.";
    case "account_locked":
      return "Your account is locked.";
    case "not_found":
      return `That ${box.singular} is gone.`;
    default:
      return err.message || "Something went wrong. Try again.";
  }
}

// ---- mutations ---------------------------------------------------------------------------------

export interface CreateForgeBoxInput {
  name: string;
  imageUrl: string;
  creatorCutPercent: number;
  rows: readonly ForgeRow[];
}

/** The box page shows the new version at once: the write answered with it. */
function applyForgeWrite(res: ForgeBoxWriteResponse): void {
  queryClient.setQueryData(qk.box(res.box.id), res.detail);
  invalidateAfterForgeChange();
}

/** POST /v1/forge/boxes. */
export function useCreateForgeBox() {
  return useMutation({
    mutationFn: (input: CreateForgeBoxInput) =>
      lootFetch<ForgeBoxWriteResponse>("/forge/boxes", {
        method: "POST",
        body: JSON.stringify({
          name: input.name.trim(),
          imageUrl: input.imageUrl,
          creatorCutPercent: input.creatorCutPercent,
          dropTable: toDropTableInput(input.rows),
        }),
      }),
    onSuccess: applyForgeWrite,
  });
}

export interface UpdateForgeDropTableInput {
  creatorCutPercent: number;
  rows: readonly ForgeRow[];
  expectedVersion: number;
}

/** PUT /v1/forge/boxes/{boxId}/drop-table, pinned to the version the builder loaded. */
export function useUpdateForgeDropTable(boxId: string) {
  return useMutation({
    mutationFn: (input: UpdateForgeDropTableInput) =>
      lootFetch<ForgeBoxWriteResponse>(`/forge/boxes/${encodeURIComponent(boxId)}/drop-table`, {
        method: "PUT",
        body: JSON.stringify({
          creatorCutPercent: input.creatorCutPercent,
          dropTable: toDropTableInput(input.rows),
          expectedVersion: input.expectedVersion,
        }),
      }),
    onSuccess: applyForgeWrite,
  });
}

/** PATCH /v1/forge/boxes/{boxId}: archive, unarchive or delete for good. */
export function useSetForgeBoxStatus() {
  return useMutation({
    mutationFn: (input: { boxId: string; status: ForgeBoxStatusValue }) =>
      lootFetch<ForgeBoxStatusResponse>(`/forge/boxes/${encodeURIComponent(input.boxId)}`, {
        method: "PATCH",
        body: JSON.stringify({ status: input.status }),
      }),
    onSuccess: (res) => invalidateAfterForgeChange(res.box.id),
  });
}

/** POST /v1/me/forge/earnings/claim. One key per click: a retried request replays the claim. */
export function useClaimForgeEarnings() {
  return useMutation({
    mutationFn: () =>
      lootFetch<ForgeClaimResponse>("/me/forge/earnings/claim", {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
      }),
    onSuccess: invalidateAfterForgeClaim,
  });
}
